import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  CartesianGrid,
} from 'recharts';
import {
  useWatchlistsQuery,
  useUpdateWatchlistMutation,
  useCreateWatchlistMutation,
  useDeleteWatchlistMutation,
} from '../../growth/api/watchlists.queries';
import { useGrowthQuery } from '../../growth/api/growth.queries';
import {
  PERIODS,
  FUNDAMENTAL_METRICS,
  FUND_FILTER_MODES,
  createDefaultFilters,
  createDefaultFundFilters,
  sanitizeGrowthState,
  WATCHLIST_STATE_VERSION,
  normalizeGrowthFilters,
  GROWTH_COMPARATORS,
} from '../../growth/lib/watchlistState';
import {
  useBacktestRunsQuery,
  useBacktestRunQuery,
  useBacktestSymbolTimelineQuery,
  useBacktestAvailabilityQuery,
} from '../api/runs.api';
import { buildUrl } from '../../../shared/config/conn';
import { useLivePricesQuery } from '../../prices/api/live.queries';
import SymbolWithLivePrice from '../../../shared/components/SymbolWithLivePrice';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const emptyMeta = { country: null, symbol_type: null, industries: null };
const PERIOD_LABELS = PERIODS.reduce((acc, { key, label }) => {
  acc[key] = label;
  return acc;
}, {});
const FUND_LABELS = FUNDAMENTAL_METRICS.reduce((acc, { key, label }) => {
  acc[key] = label;
  return acc;
}, {});
const META_LABELS = {
  country: 'Country',
  symbol_type: 'Type',
};

const GROWTH_SYMBOLS = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
};

function buildGrowthSummary(filters) {
  const source = filters || {};
  return PERIODS.map(({ key, label }) => {
    const entry = source[key];
    if (!entry) return null;
    const rawValue = entry.value;
    if (rawValue === undefined || rawValue === null || rawValue === '') return null;
    const numeric = Number(rawValue);
    const text = Number.isFinite(numeric) ? numeric : rawValue;
    const symbol = GROWTH_SYMBOLS[entry.comparator] || '≥';
    return `${label} ${symbol} ${text}%`;
  }).filter(Boolean);
}

function buildFundSummary(filters) {
  if (!filters) return [];
  return FUNDAMENTAL_METRICS.map(({ key, label }) => {
    const entry = filters[key];
    if (!entry || entry.mode === 'off') return null;
    const value = entry.value;
    const formatVal = (val) => {
      if (val === undefined || val === null || val === '') return '';
      const numeric = Number(val);
      if (!Number.isFinite(numeric)) return String(val);
      if (key === 'enterprise_value' || key === 'ebitda') {
        return formatCurrency(numeric);
      }
      return numeric.toFixed(2);
    };
    switch (entry.mode) {
      case 'has':
        return `${label}: has data`;
      case 'missing':
        return `${label}: missing data`;
      case 'gt':
        if (value === undefined || value === null || value === '') return null;
        return `${label} ≥ ${formatVal(value)}`;
      case 'lt':
        if (value === undefined || value === null || value === '') return null;
        return `${label} ≤ ${formatVal(value)}`;
      default:
        return null;
    }
  }).filter(Boolean);
}

function buildMetaSummary(filters) {
  if (!filters) return [];
  const values = [];
  if (Array.isArray(filters.country) && filters.country.length) {
    values.push(`Country: ${filters.country.join(', ')}`);
  }
  if (Array.isArray(filters.symbol_type) && filters.symbol_type.length) {
    values.push(`Type: ${filters.symbol_type.join(', ')}`);
  }
  return values;
}
const LIVE_RUN_STORAGE_KEY = 'backtesting_active_run_id';
const DEFAULT_PROFILE_NAME = 'New profile';

function formatCurrency(value) {
  if (value == null || Number.isNaN(value)) return '$0.00';
  return currencyFormatter.format(value);
}

function FilterSummary({ growthFilters, fundFilters, metaFilters, pinnedSymbols, restrictPinned }) {
  if (
    !growthFilters?.length &&
    !fundFilters?.length &&
    !metaFilters?.length &&
    !(restrictPinned && Array.isArray(pinnedSymbols) && pinnedSymbols.length)
  ) {
    return null;
  }

  const sections = [];
  if (Array.isArray(growthFilters) && growthFilters.length) {
    sections.push({ label: 'Growth thresholds', values: growthFilters });
  }
  if (Array.isArray(fundFilters) && fundFilters.length) {
    sections.push({ label: 'Fundamental filters', values: fundFilters });
  }
  if (Array.isArray(metaFilters) && metaFilters.length) {
    sections.push({ label: 'Meta filters', values: metaFilters });
  }
  if (restrictPinned && Array.isArray(pinnedSymbols) && pinnedSymbols.length) {
    sections.push({ label: 'Pinned symbols', values: [`Restricted to ${pinnedSymbols.length}`] });
  }

  if (!sections.length) return null;

  return (
    <div className="filter-summary">
      {sections.map((section) => (
        <div key={section.label} className="filter-summary__section">
          <div className="filter-summary__title">{section.label}</div>
          <div className="filter-summary__values">{section.values.join(', ')}</div>
        </div>
      ))}
    </div>
  );
}

function MultiSelectFilter({
  label,
  options,
  selected,
  search,
  onSearchChange,
  onToggle,
  onSetAll,
  onSetNone,
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [panelStyle, setPanelStyle] = React.useState(null);
  const panelRef = React.useRef(null);
  const triggerRef = React.useRef(null);

  const closePanel = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const updatePanelPosition = React.useCallback(() => {
    if (typeof window === 'undefined' || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const MIN_WIDTH = 320;
    const MAX_WIDTH = 480;
    const ideal = Math.max(rect.width * 2, MIN_WIDTH);
    const width = Math.min(Math.max(ideal, MIN_WIDTH), Math.min(MAX_WIDTH, viewportWidth - 32));
    const left = Math.min(Math.max(rect.left + rect.width / 2 - width / 2, 16), viewportWidth - width - 16);
    const safeHeight = Math.max(280, Math.min(520, viewportHeight - 48));
    const desiredTop = rect.bottom + 12;
    const maxTop = viewportHeight ? viewportHeight - safeHeight - 16 : desiredTop;
    const clampedTop = Math.max(16, Math.min(desiredTop, Math.max(16, maxTop)));
    setPanelStyle({ left, top: clampedTop, width });
  }, []);

  const handleTriggerClick = () => {
    setIsOpen((prev) => {
      if (prev) return false;
      updatePanelPosition();
      return true;
    });
  };

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const handleResize = () => updatePanelPosition();
    const handleScroll = () => updatePanelPosition();
    const handleKey = (event) => {
      if (event.key === 'Escape') closePanel();
    };
    const handleClick = (event) => {
      if (panelRef.current && panelRef.current.contains(event.target)) return;
      if (triggerRef.current && triggerRef.current.contains(event.target)) return;
      closePanel();
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [closePanel, isOpen, updatePanelPosition]);

  const effectiveSelected = selected === null ? null : selected;
  const filteredOptions = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) => opt.toLowerCase().includes(term));
  }, [options, search]);

  const checkedCount = effectiveSelected === null ? options.length : effectiveSelected.length;
  const summaryLabel = options.length && checkedCount !== options.length
    ? `${label} (${checkedCount}/${options.length})`
    : label;
  const isChecked = (option) => effectiveSelected === null || effectiveSelected.includes(option);

  return (
    <div className="multi-filter">
      <button
        type="button"
        ref={triggerRef}
        className="ghost sm multi-filter-trigger"
        onClick={handleTriggerClick}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {summaryLabel}
      </button>
      {isOpen && panelStyle && typeof document !== 'undefined'
        ? ReactDOM.createPortal(
          <div className="multi-filter-overlay" role="presentation">
            <div className="multi-filter-backdrop" onClick={closePanel} />
            <div
              className="multi-filter-panel"
              ref={panelRef}
              role="dialog"
              aria-label={`${label} filter options`}
              style={{
                left: `${panelStyle.left}px`,
                top: `${panelStyle.top}px`,
                width: `${panelStyle.width}px`,
              }}
            >
              <header className="multi-filter-header">
                <span>{label}</span>
                <div className="multi-filter-actions">
                  <button type="button" className="ghost xs" onClick={onSetAll}>
                    All
                  </button>
                  <button type="button" className="ghost xs" onClick={onSetNone} disabled={options.length === 0}>
                    None
                  </button>
                </div>
              </header>
              <input
                className="multi-filter-search"
                type="search"
                value={search}
                placeholder="Search..."
                onChange={(evt) => onSearchChange(evt.target.value)}
              />
              <div className="multi-filter-options">
                {filteredOptions.length === 0 ? (
                  <p className="subtle">No matches.</p>
                ) : (
                  filteredOptions.map((option) => (
                    <label key={option} className="multi-filter-option">
                      <input
                        type="checkbox"
                        checked={isChecked(option)}
                        onChange={() => onToggle(option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

function BacktestChart({ data }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  return (
    <div className="backtest-chart">
      <h4>Equity Curve</h4>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 10, right: 24, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <ChartTooltip
            content={({ active, payload, label }) => {
              if (!active || !payload || payload.length === 0) return null;
              const point = payload[0].payload;
              return (
                <div className="chart-tooltip">
                  <div>{label}</div>
                  <div>Equity: {formatCurrency(point.equity)}</div>
                  <div>Realized: {formatCurrency(point.realized)}</div>
                  <div>Unrealized: {formatCurrency(point.unrealized)}</div>
                </div>
              );
            }}
          />
          <Line type="monotone" dataKey="equity" stroke="#34d399" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function AvailabilityChart({ data, loading }) {
  if (loading) {
    return (
      <div className="backtest-panel availability-panel">
        <h4>Tradeable Symbols</h4>
        <p className="subtle">Loading availability…</p>
      </div>
    );
  }
  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }
  return (
    <div className="backtest-panel availability-panel">
      <h4>Tradeable Symbols</h4>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 10, right: 24, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={30} />
          <YAxis tick={{ fontSize: 12 }} />
          <ChartTooltip
            content={({ active, payload, label }) => {
              if (!active || !payload || payload.length === 0) return null;
              const point = payload[0].payload;
              return (
                <div className="chart-tooltip">
                  <div>{label}</div>
                  <div>Symbols: {point.symbols.toLocaleString()}</div>
                </div>
              );
            }}
          />
          <Line type="monotone" dataKey="symbols" stroke="#60a5fa" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function BacktestEvents({ events, livePriceMap }) {
  if (!Array.isArray(events) || events.length === 0) {
    return <div className="backtest-panel empty">No trades were generated for this configuration.</div>;
  }
  return (
    <div className="backtest-panel">
      <h4>Trade Timeline</h4>
      <div className="backtest-table">
        <div className="backtest-table__header">
          <span>Date</span>
          <span>Type</span>
          <span>Symbol</span>
          <span>Price</span>
          <span>Reason</span>
          <span>P&L</span>
        </div>
        <div className="backtest-table__body">
          {events.map((event, idx) => (
            <div key={`${event.date}-${event.type}-${event.symbol}-${idx}`} className="backtest-table__row">
              <span>{event.date}</span>
              <span className={`badge badge--${event.type}`}>{event.type}</span>
              <span>
                <SymbolWithLivePrice symbol={event.symbol} price={livePriceMap?.[event.symbol]} />
              </span>
              <span>{formatCurrency(event.price)}</span>
              <span>{event.reason || '-'}</span>
              <span className={event.realized_pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                {event.type === 'sell' ? formatCurrency(event.realized_pnl) : '—'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BacktestSummary({ summary }) {
  if (!summary) return null;

  const cards = [
    { label: 'Final Equity', value: summary.final_equity, type: 'currency' },
    { label: 'Realized P&L', value: summary.realized_pnl, type: 'currency' },
    { label: 'Unrealized P&L', value: summary.unrealized_pnl, type: 'currency' },
    { label: 'Buys / Sells', value: `${summary.total_buys} / ${summary.total_sells}` },
    { label: 'Window', value: `${summary.start_date} → ${summary.end_date}` },
    { label: 'Capital Added', value: summary.capital_added, type: 'currency' },
    { label: 'Cash Balance', value: summary.cash_balance, type: 'currency' },
  ];

  const renderValue = (card) => {
    if (card.type === 'currency') {
      const classes = ['backtest-card__value'];
      if (typeof card.value === 'number') {
        classes.push(card.value >= 0 ? 'pnl-positive' : 'pnl-negative');
      }
      return <span className={classes.join(' ')}>{formatCurrency(card.value)}</span>;
    }
    return <span className="backtest-card__value">{card.value}</span>;
  };

  return (
    <div className="backtest-summary">
      <div className="backtest-summary__stats">
        {cards.map((card) => (
          <div key={card.label} className="backtest-card">
            <span className="backtest-card__label">{card.label}</span>
            {renderValue(card)}
          </div>
        ))}
      </div>
    </div>
  );
}

function BacktestUniverseCard({ universe, onSelectSymbol, activeSymbol, livePriceMap }) {
  const [showAllSymbols, setShowAllSymbols] = useState(false);
  const [symbolQuery, setSymbolQuery] = useState('');
  const hasUniverse = Array.isArray(universe?.symbols);
  const boughtSymbols = hasUniverse ? universe.symbols : [];
  const skippedSymbols = Array.isArray(universe?.skipped_symbols) ? universe.skipped_symbols : [];
  useEffect(() => {
    setSymbolQuery('');
    setShowAllSymbols(false);
  }, [universe?.symbols, universe?.skipped_symbols]);
  const query = symbolQuery.trim().toUpperCase();
  const filterEntries = (entries) =>
    entries.filter((entry) => (entry.symbol || '').toUpperCase().includes(query));
  const filteredBought = useMemo(
    () => filterEntries(boughtSymbols.map((symbol) => ({ symbol, type: 'bought' }))),
    [boughtSymbols, query],
  );
  const filteredSkipped = useMemo(() => filterEntries(skippedSymbols), [skippedSymbols, query]);
  const combinedEntries = useMemo(
    () => [...filteredBought, ...filteredSkipped],
    [filteredBought, filteredSkipped],
  );
  const activeToken = (activeSymbol || '').toUpperCase();

  return (
    <div className="backtest-card backtest-card--universe">
      <div className="backtest-card__label">Universe</div>
      <div className="backtest-card__value">
        {hasUniverse
          ? boughtSymbols.length
            ? `${boughtSymbols.length} bought symbols`
            : 'No buys recorded'
          : 'Universe not available'}
      </div>
      {(hasUniverse && boughtSymbols.length > 0) || skippedSymbols.length > 0 ? (
        <>
          <div className="universe-filter universe-filter--toggle">
            <input
              type="text"
              placeholder="Search symbols"
              value={symbolQuery}
              onChange={(event) => setSymbolQuery(event.target.value)}
              disabled={!hasUniverse && skippedSymbols.length === 0}
            />
            {skippedSymbols.length > 0 && hasUniverse && (
              <button
                type="button"
                className="ghost xs"
                onClick={() => setShowAllSymbols((prev) => !prev)}
              >
                {showAllSymbols ? 'Show bought only' : 'Show all (with reasons)'}
              </button>
            )}
          </div>
          {!showAllSymbols && hasUniverse && filteredBought.length > 0 && (
            <div className="universe-list">
              {filteredBought.map((entry) => {
                const token = entry.symbol.toUpperCase();
                const isActive = activeToken && activeToken === token;
                return (
                  <button
                    type="button"
                    key={entry.symbol}
                    className={`universe-list__item${isActive ? ' universe-list__item--active' : ''}`}
                    onClick={() => onSelectSymbol && onSelectSymbol(entry.symbol)}
                  >
                    <SymbolWithLivePrice symbol={entry.symbol} price={livePriceMap?.[entry.symbol]} />
                  </button>
                );
              })}
              {filteredBought.length === 0 && <span className="universe-list__empty">No matches</span>}
            </div>
          )}
          {showAllSymbols && hasUniverse && (
            <div className="universe-list universe-list--all">
              {combinedEntries.length === 0 ? (
                <span className="universe-list__empty">No matches</span>
              ) : (
                combinedEntries.map((entry) => (
                  <button
                    type="button"
                    key={`${entry.symbol}-${entry.reason || entry.type}`}
                    className="universe-all-entry"
                    onClick={() => onSelectSymbol && onSelectSymbol(entry.symbol)}
                  >
                    <span className="universe-all-entry__symbol">
                      <SymbolWithLivePrice symbol={entry.symbol} price={livePriceMap?.[entry.symbol]} />
                    </span>
                    <span className={`universe-all-entry__reason ${entry.type === 'bought' ? 'ok' : 'warn'}`}>
                      {entry.type === 'bought'
                        ? 'bought'
                        : (entry.reason || 'unknown').replace(/_/g, ' ')}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function BacktestPositions({ positions, livePriceMap }) {
  if (!Array.isArray(positions) || positions.length === 0) return null;
  return (
    <div className="backtest-panel">
      <h4>Open Positions</h4>
      <div className="backtest-table">
        <div className="backtest-table__header">
          <span>Symbol</span>
          <span>Entry Date</span>
          <span>Entry Price</span>
          <span>Last Price</span>
          <span>Unrealized P&L</span>
        </div>
        <div className="backtest-table__body">
          {positions.map((pos) => (
            <div key={`${pos.symbol}-${pos.entry_date}`} className="backtest-table__row">
              <span>
                <SymbolWithLivePrice symbol={pos.symbol} price={livePriceMap?.[pos.symbol]} />
              </span>
              <span>{pos.entry_date}</span>
              <span>{formatCurrency(pos.entry_price)}</span>
              <span>{formatCurrency(pos.current_price)}</span>
              <span className={pos.unrealized_pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                {formatCurrency(pos.unrealized_pnl)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SymbolPointTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload;
  if (!point) return null;
  const sellEvents = Array.isArray(point.events) ? point.events.filter((evt) => evt.type === 'sell') : [];
  const buyEvents = Array.isArray(point.events) ? point.events.filter((evt) => evt.type === 'buy') : [];
  const baseConditions = Array.isArray(point.conditions) ? point.conditions : [];
  const nonFundamentalConditions = baseConditions.filter((condition) => condition.category !== 'fundamental');
  const fundamentalSnapshot = point.filters?.fundamental || {};
  const fundamentals = point.fundamentals || {};
  const fundamentalConditions = Object.entries(fundamentalSnapshot).reduce((acc, [metric, cfg]) => {
    if (!cfg || cfg.mode === 'off') return acc;
    const mode = cfg.mode;
    const rawValue = fundamentals[metric];
    const thresholdValue = cfg.value;
    let met = null;
    if (mode === 'has') {
      met = rawValue != null;
    } else if (mode === 'missing') {
      met = rawValue == null;
    } else if (thresholdValue !== undefined && thresholdValue !== null && thresholdValue !== '') {
      const numericThreshold = Number(thresholdValue);
      if (Number.isFinite(numericThreshold)) {
        const numericValue = rawValue != null ? Number(rawValue) : null;
        if (mode === 'gt') {
          met = numericValue != null && numericValue >= numericThreshold;
        } else if (mode === 'lt') {
          met = numericValue != null && numericValue <= numericThreshold;
        }
      }
    }
    acc.push({
      metric,
      value: rawValue,
      threshold: thresholdValue,
      expected: thresholdValue,
      met,
      mode,
      category: 'fundamental',
    });
    return acc;
  }, []);
  const conditions = nonFundamentalConditions.concat(fundamentalConditions);

  const describeCondition = (condition) => {
    const category = condition.category || 'growth';
    const metric = condition.metric;
    const mode = condition.mode || 'gt';
    const labelMap =
      category === 'fundamental'
        ? FUND_LABELS
        : category === 'meta'
          ? META_LABELS
          : PERIOD_LABELS;
    const labelText = labelMap[metric] || metric;
    if (category === 'growth') {
      const valueNumber = Number(condition.value);
      const thresholdNumber = Number(condition.threshold);
      const valueText = Number.isFinite(valueNumber) ? `${valueNumber.toFixed(2)}%` : '—';
      const thresholdText = Number.isFinite(thresholdNumber) ? `${thresholdNumber.toFixed(2)}%` : '—';
      return `${labelText}: ${valueText} (≥ ${thresholdText})`;
    }

    const formatFundValue = (val) => {
      if (val == null) return '—';
      const numeric = Number(val);
      if (!Number.isFinite(numeric)) return '—';
      if (metric === 'enterprise_value' || metric === 'ebitda') {
        return formatCurrency(numeric);
      }
      return numeric.toFixed(2);
    };

    if (category === 'fundamental') {
      if (mode === 'has') {
        return `${labelText}: ${condition.met ? 'has data' : 'missing data'}`;
      }
      if (mode === 'missing') {
        return `${labelText}: ${condition.met ? 'missing data' : 'has data'}`;
      }
      const comparator = mode === 'lt' ? '≤' : '≥';
      const thresholdSource = condition.threshold ?? condition.expected;
      const threshText = thresholdSource != null ? formatFundValue(thresholdSource) : '—';
      return `${labelText}: ${formatFundValue(condition.value)} (${comparator} ${threshText})`;
    }

    if (mode === 'has') {
      return `${labelText}: ${condition.met ? 'has data' : 'missing data'}`;
    }
    if (mode === 'missing') {
      return `${labelText}: ${condition.met ? 'missing data' : 'has data'}`;
    }
    if ((mode === 'gt' || mode === 'lt') && condition.threshold != null) {
      const comparator = mode === 'gt' ? '>' : '<';
      return `${labelText}: ${formatFundValue(condition.value)} (${comparator} ${formatFundValue(condition.threshold)})`;
    }
    return `${labelText}: ${formatFundValue(condition.value)}`;
  };

  const statusClass = (condition) => {
    if (condition.met === true) return 'pnl-positive';
    if (condition.met === false) return 'pnl-negative';
    return '';
  };

  return (
    <div className="chart-tooltip symbol-tooltip">
      <div className="symbol-tooltip__header">
        <strong>{label}</strong>
        <span className={point.holding ? 'pnl-positive' : 'pnl-negative'}>
          {point.holding ? 'Holding' : 'Flat'}
        </span>
      </div>
      <div>Price: {formatCurrency(point.price)}</div>
      {conditions.length > 0 && (
        <div className="symbol-tooltip__conditions">
          <div>Filters:</div>
          {conditions.map((condition, idx) => (
            <div key={`${condition.metric}-${idx}`} className={statusClass(condition)}>
              {describeCondition(condition)}
            </div>
          ))}
        </div>
      )}
      {sellEvents.length > 0 && (
        <div className="symbol-tooltip__events">
          {sellEvents.map((event, idx) => (
            <div key={`sell-${idx}`}>
              Sold @ {formatCurrency(event.price)} ({event.reason || 'unknown'})
            </div>
          ))}
        </div>
      )}
      {sellEvents.length === 0 && buyEvents.length > 0 && (
        <div className="symbol-tooltip__events">
          {buyEvents.map((event, idx) => (
            <div key={`buy-${idx}`}>Bought @ {formatCurrency(event.price)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function SymbolTimelinePanel({ runId, symbol, livePriceMap }) {
  const { data, isLoading, isError, error } = useBacktestSymbolTimelineQuery(runId, symbol, {
    enabled: Boolean(runId && symbol),
  });

  if (!runId) return null;

  if (!symbol) {
    return <div className="backtest-panel empty">Select a symbol to inspect its timeline.</div>;
  }

  if (isLoading) {
    return <div className="backtest-panel empty">Loading {symbol} timeline…</div>;
  }

  if (isError) {
    return (
      <div className="backtest-panel empty">
        {error?.message || 'Unable to load symbol timeline.'}
      </div>
    );
  }

  if (!data || !Array.isArray(data.points) || data.points.length === 0) {
    return <div className="backtest-panel empty">No timeline data for {symbol}.</div>;
  }

  const chartData = data.points.map((point) => ({
    ...point,
    priceHolding: point.holding ? point.price : null,
    priceFlat: point.holding ? null : point.price,
  }));

  return (
    <div className="backtest-panel symbol-panel">
      <h4>
        <SymbolWithLivePrice symbol={symbol} price={livePriceMap?.[symbol]} /> Timeline{' '}
        <span className="symbol-panel__window">
          {data.start_date} → {data.end_date}
        </span>
      </h4>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData} margin={{ top: 10, right: 24, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 12 }} domain={['auto', 'auto']} />
          <ChartTooltip content={<SymbolPointTooltip />} />
          <Line type="monotone" dataKey="priceHolding" stroke="#34d399" strokeWidth={2} dot={false} name="Holding" />
          <Line type="monotone" dataKey="priceFlat" stroke="#f87171" strokeWidth={2} dot={false} name="Flat" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function BacktestAuditTable({ rows, livePriceMap }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const renderTrades = (list, kind) => {
    if (!Array.isArray(list) || list.length === 0) return <span className="audit-empty">—</span>;
    return (
      <div className="audit-chip-row">
        {list.map((trade, idx) => (
          <span key={`${trade.symbol}-${trade.date}-${idx}`} className={`audit-chip audit-chip--${kind}`}>
            <SymbolWithLivePrice symbol={trade.symbol} price={livePriceMap?.[trade.symbol]} /> @ {formatCurrency(trade.price)}
          </span>
        ))}
      </div>
    );
  };

  const renderHoldings = (list) => {
    if (!Array.isArray(list) || list.length === 0) return <span className="audit-empty">—</span>;
    return (
      <div className="audit-chip-column">
        {list.map((holding) => (
          <div key={`${holding.symbol}-${holding.entry_date}`} className="audit-holding">
            <strong>
              <SymbolWithLivePrice symbol={holding.symbol} price={livePriceMap?.[holding.symbol]} />
            </strong>
            <span>
              {holding.shares} @ {formatCurrency(holding.entry_price)} → {formatCurrency(holding.current_price)}
            </span>
            <span className={holding.unrealized_pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}>
              {formatCurrency(holding.unrealized_pnl)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="backtest-panel">
      <h4>Daily Audit</h4>
      <div className="audit-table">
        <div className="audit-table__row audit-table__header">
          <span>Date</span>
          <span>Buys</span>
          <span>Sells</span>
          <span>Holdings</span>
          <span>Equity</span>
        </div>
        <div className="audit-table__body">
          {rows.map((row) => (
            <div key={row.date} className="audit-table__row">
              <span className="audit-date">{row.date}</span>
              <span>{renderTrades(row.buys, 'buy')}</span>
              <span>{renderTrades(row.sells, 'sell')}</span>
              <span>{renderHoldings(row.holdings)}</span>
              <span className="audit-equity">
                <strong>{formatCurrency(row.equity)}</strong>
                <em>Realized: {formatCurrency(row.realized)}</em>
                <em>Unrealized: {formatCurrency(row.unrealized)}</em>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function BacktestingView() {
  const [selectedWatchlistId, setSelectedWatchlistId] = useState(null);
  const [activeProfile, setActiveProfile] = useState(null);
  const [stopLossMode, setStopLossMode] = useState('trailing');
  const [growthInputs, setGrowthInputs] = useState(() => normalizeGrowthFilters(createDefaultFilters()));
  const [fundFilters, setFundFilters] = useState(createDefaultFundFilters());
  const [metaFilters, setMetaFilters] = useState(emptyMeta);
  const [countrySearch, setCountrySearch] = useState('');
  const [symbolTypeSearch, setSymbolTypeSearch] = useState('');
  const [industrySearch, setIndustrySearch] = useState('');
  const [pinnedSymbols, setPinnedSymbols] = useState([]);
  const [restrictPinned, setRestrictPinned] = useState(false);
  const [frequencyInput, setFrequencyInput] = useState('5');
  const [backtestStartDate, setBacktestStartDate] = useState('');
  const [backtestEndDate, setBacktestEndDate] = useState('');
  const [watchlistError, setWatchlistError] = useState(null);
  const [watchlistMessage, setWatchlistMessage] = useState(null);
  const [isWatchlistOpen, setIsWatchlistOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState(DEFAULT_PROFILE_NAME);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [nameModalValue, setNameModalValue] = useState('');
  const [nameModalError, setNameModalError] = useState(null);
  const [pendingOverwriteId, setPendingOverwriteId] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [formError, setFormError] = useState(null);
  const [lastRunPayload, setLastRunPayload] = useState(null);
  const [liveRunId, setLiveRunId] = useState(() => {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(LIVE_RUN_STORAGE_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  });
  const [liveRunMeta, setLiveRunMeta] = useState(null);
  const [liveStatus, setLiveStatus] = useState('idle');
  const [liveEquityPoints, setLiveEquityPoints] = useState([]);
  const [liveTradeEvents, setLiveTradeEvents] = useState([]);
  const [liveMessages, setLiveMessages] = useState([]);
  const [liveResult, setLiveResult] = useState(null);
  const [liveProgress, setLiveProgress] = useState(null);
  const [liveProgressState, setLiveProgressState] = useState({ completed: 0, total: 0 });
  const liveProgressPercent = useMemo(() => {
    if (!liveProgressState.total || liveProgressState.total <= 0) return 0;
    return Math.min(100, Math.round((liveProgressState.completed / liveProgressState.total) * 100));
  }, [liveProgressState]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [resultsTab, setResultsTab] = useState('equity');
  const [hasBootstrappedProfile, setHasBootstrappedProfile] = useState(false);
  const selectedRunIdRef = useRef(null);
  const wsRef = useRef(null);
  const pendingMessagesRef = useRef([]);
  const reconnectTimerRef = useRef(null);
  const activeRunIdRef = useRef(null);
  const liveStatusRef = useRef('idle');
  const needsInitialSubscribe = useRef(liveRunId != null);
  const viewingRunIdRef = useRef(null);
  const [viewedRunDetail, setViewedRunDetail] = useState(null);
  const selectedRunDetail = viewedRunDetail;
  const [liveDailyStats, setLiveDailyStats] = useState([]);
  const [runList, setRunList] = useState([]);
  const nameInputRef = useRef(null);
  const nameModalInputRef = useRef(null);
  const toastTimerRef = useRef(null);
  const lastSavedPayloadRef = useRef(null);
  const defaultProfileStateRef = useRef(sanitizeGrowthState({}));

  const {
    data: watchlistsData,
    isLoading: watchlistsLoading,
    isError: watchlistsIsError,
    error: watchlistsError,
    refetch: refetchWatchlists,
  } = useWatchlistsQuery();
  const { data: availabilityData, isLoading: availabilityLoading } = useBacktestAvailabilityQuery();
  const { data: growthData } = useGrowthQuery();
  const { mutateAsync: createWatchlistMutateAsync, isPending: isCreateWatchlistPending } = useCreateWatchlistMutation();
  const { mutateAsync: updateWatchlistMutateAsync, isPending: isUpdateWatchlistPending } = useUpdateWatchlistMutation();
  const { mutateAsync: deleteWatchlistMutateAsync } = useDeleteWatchlistMutation();
  const {
    data: runsData,
    isLoading: runsLoading,
    isError: runsIsError,
    error: runsError,
    refetch: refetchRuns,
  } = useBacktestRunsQuery();
  const runDetailQuery = useBacktestRunQuery(selectedRunId, { enabled: Boolean(selectedRunId) });
  const isRunDetailLoading = Boolean(selectedRunId && runDetailQuery.isFetching);

  const syncUrl = useCallback((id) => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (id != null) {
      url.searchParams.set('watchlist', String(id));
    } else {
      url.searchParams.delete('watchlist');
    }
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const [pendingWatchlistId, setPendingWatchlistId] = useState(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const idParam = params.get('watchlist');
    const parsed = idParam ? Number(idParam) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  });

  useEffect(() => {
    if (Array.isArray(runsData)) {
      setRunList(runsData);
    }
  }, [runsData]);

  useEffect(() => {
    if (selectedRunDetail || liveResult || liveRunId) {
      setResultsTab('equity');
    }
  }, [selectedRunDetail, liveResult, liveRunId]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  useEffect(() => {
    if (isNameModalOpen && nameModalInputRef.current) {
      nameModalInputRef.current.focus();
      nameModalInputRef.current.select();
    }
  }, [isNameModalOpen]);

  useEffect(() => {
    if (!watchlistMessage) {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
      return undefined;
    }
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      setWatchlistMessage(null);
      toastTimerRef.current = null;
    }, 3000);
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, [watchlistMessage]);

  const watchlistOptions = useMemo(
    () => (Array.isArray(watchlistsData) ? watchlistsData : []),
    [watchlistsData],
  );

  const applyWatchlistState = useCallback((rawState, options = {}) => {
    const { markSaved = true } = options;
    const sanitized = sanitizeGrowthState(rawState);
    setGrowthInputs(normalizeGrowthFilters(sanitized.filters));
    setFundFilters({ ...sanitized.fundFilters });
    setMetaFilters(sanitized.metaFilters || emptyMeta);
    setPinnedSymbols(sanitized.pinnedSymbols || []);
    setRestrictPinned(Boolean(sanitized.restrictPinned));
    setStopLossMode(sanitized.stopLoss?.mode || 'trailing');
    if (markSaved) {
      lastSavedPayloadRef.current = JSON.stringify({
        version: WATCHLIST_STATE_VERSION,
        ...sanitized,
      });
    } else {
      lastSavedPayloadRef.current = null;
    }
    return sanitized;
  }, []);

  const baseWatchlistState = activeProfile?.state ?? defaultProfileStateRef.current;

  const currentStatePayload = useMemo(() => {
    const normalizedMeta = {
      country: metaFilters.country ?? null,
      symbol_type: metaFilters.symbol_type ?? null,
      industries: metaFilters.industries ?? null,
    };
    const stopLossValue = stopLossMode === 'fixed_drop'
      ? (baseWatchlistState.stopLoss?.value ?? 0.05)
      : 0.05;
    return {
      version: WATCHLIST_STATE_VERSION,
      ...baseWatchlistState,
      filters: normalizeGrowthFilters(growthInputs),
      fundFilters: { ...fundFilters },
      metaFilters: normalizedMeta,
      pinnedSymbols: [...pinnedSymbols],
      stopLoss: { mode: stopLossMode, value: stopLossValue },
      restrictPinned,
    };
  }, [baseWatchlistState, fundFilters, growthInputs, metaFilters, pinnedSymbols, restrictPinned, stopLossMode]);

  const hasActiveProfile = Boolean(activeProfile);
  const currentPayloadString = useMemo(() => JSON.stringify(currentStatePayload), [currentStatePayload]);
  const hasChangesFromActive = !hasActiveProfile || currentPayloadString !== lastSavedPayloadRef.current;
  const savingProfile = hasActiveProfile
    ? pendingOverwriteId !== null || isUpdateWatchlistPending
    : isCreateWatchlistPending;
  const watchlistsFetchError = watchlistsIsError
    ? (watchlistsError instanceof Error
      ? watchlistsError.message
      : String(watchlistsError ?? 'Unable to load profiles'))
    : null;

  const handleNewProfile = useCallback(() => {
    applyWatchlistState(null, { markSaved: false });
    setActiveProfile(null);
    setSelectedWatchlistId(null);
    setNameDraft(DEFAULT_PROFILE_NAME);
    setIsEditingName(false);
    setWatchlistError(null);
    setWatchlistMessage(null);
    setPendingWatchlistId(null);
    setHasBootstrappedProfile(true);
    syncUrl(null);
  }, [applyWatchlistState, syncUrl]);

  const handleLoadWatchlist = useCallback(
    (watchlist, options = {}) => {
      if (!watchlist) return;
      const { closePanel = true } = options;
      const sanitized = applyWatchlistState(watchlist.payload, { markSaved: true });
      setActiveProfile({ id: watchlist.id, name: watchlist.name, state: sanitized });
      setSelectedWatchlistId(watchlist.id);
      setNameDraft(watchlist.name);
      setIsEditingName(false);
      setWatchlistError(null);
      setWatchlistMessage(null);
      setPendingWatchlistId(null);
      setHasBootstrappedProfile(true);
      syncUrl(watchlist.id);
      if (closePanel) {
        setIsWatchlistOpen(false);
      }
    },
    [applyWatchlistState, syncUrl],
  );

  useEffect(() => {
    if (!watchlistOptions.length) return;
    if (pendingWatchlistId != null) {
      const target = watchlistOptions.find((item) => item.id === pendingWatchlistId);
      if (target) {
        handleLoadWatchlist(target, { closePanel: false });
        setPendingWatchlistId(null);
      } else if (!watchlistsLoading) {
        setPendingWatchlistId(null);
        syncUrl(null);
      }
      return;
    }
    if (!hasBootstrappedProfile) {
      handleLoadWatchlist(watchlistOptions[0], { closePanel: false });
      setHasBootstrappedProfile(true);
    }
  }, [handleLoadWatchlist, hasBootstrappedProfile, pendingWatchlistId, syncUrl, watchlistOptions, watchlistsLoading]);

  const handleCreateWatchlist = useCallback(
    async (name) => {
      setWatchlistError(null);
      setWatchlistMessage(null);
      try {
        const result = await createWatchlistMutateAsync({ name, payload: currentStatePayload });
        const sanitized = sanitizeGrowthState(currentStatePayload);
        lastSavedPayloadRef.current = JSON.stringify({
          version: WATCHLIST_STATE_VERSION,
          ...sanitized,
        });
        setActiveProfile({ id: result.id, name: result.name, state: sanitized });
        setSelectedWatchlistId(result.id);
        setNameDraft(result.name);
        setIsEditingName(false);
        setHasBootstrappedProfile(true);
        syncUrl(result.id);
        setWatchlistMessage(`Saved "${result.name}"`);
        refetchWatchlists();
        return result;
      } catch (err) {
        setWatchlistError(err instanceof Error ? err.message : 'Unable to save profile');
        return null;
      }
    },
    [createWatchlistMutateAsync, currentStatePayload, refetchWatchlists, syncUrl],
  );

  const handleOverwriteWatchlist = useCallback(
    async () => {
      if (!activeProfile) return null;
      setPendingOverwriteId(activeProfile.id);
      setWatchlistError(null);
      setWatchlistMessage(null);
      try {
        const result = await updateWatchlistMutateAsync({
          id: activeProfile.id,
          name: activeProfile.name,
          payload: currentStatePayload,
        });
        const sanitized = sanitizeGrowthState(currentStatePayload);
        lastSavedPayloadRef.current = JSON.stringify({
          version: WATCHLIST_STATE_VERSION,
          ...sanitized,
        });
        setActiveProfile({ id: result.id, name: result.name, state: sanitized });
        setWatchlistMessage(`Updated "${result.name}"`);
        refetchWatchlists();
        syncUrl(result.id);
        return result;
      } catch (err) {
        setWatchlistError(err instanceof Error ? err.message : 'Unable to update profile');
        return null;
      } finally {
        setPendingOverwriteId(null);
      }
    },
    [activeProfile, currentStatePayload, refetchWatchlists, syncUrl, updateWatchlistMutateAsync],
  );

  const handleDeleteWatchlist = useCallback(
    async (watchlist) => {
      if (!watchlist) return;
      const confirmed = typeof window === 'undefined'
        ? true
        : window.confirm(`Delete profile "${watchlist.name}"?`);
      if (!confirmed) return;
      setPendingDeleteId(watchlist.id);
      setWatchlistError(null);
      setWatchlistMessage(null);
      try {
        await deleteWatchlistMutateAsync(watchlist.id);
        if (activeProfile?.id === watchlist.id) {
          handleNewProfile();
        }
        setWatchlistMessage(`Deleted "${watchlist.name}"`);
        refetchWatchlists();
      } catch (err) {
        setWatchlistError(err instanceof Error ? err.message : 'Unable to delete profile');
      } finally {
        setPendingDeleteId(null);
      }
    },
    [activeProfile, deleteWatchlistMutateAsync, handleNewProfile, refetchWatchlists],
  );

  const handleSaveWatchlist = useCallback(() => {
    const trimmed = nameDraft.trim();
    if (!hasActiveProfile) {
      if (!trimmed || trimmed === DEFAULT_PROFILE_NAME) {
        setNameModalValue(trimmed === DEFAULT_PROFILE_NAME ? '' : trimmed);
        setNameModalError(null);
        setIsNameModalOpen(true);
        return;
      }
      const exists = watchlistOptions.some((item) => item.name.toLowerCase() === trimmed.toLowerCase());
      if (exists) {
        setWatchlistError('Profile name already exists');
        return;
      }
      handleCreateWatchlist(trimmed);
      return;
    }
    handleOverwriteWatchlist();
  }, [handleCreateWatchlist, handleOverwriteWatchlist, hasActiveProfile, nameDraft, watchlistOptions]);

  const cancelNameEdit = useCallback(() => {
    if (hasActiveProfile) {
      setNameDraft(activeProfile?.name ?? DEFAULT_PROFILE_NAME);
    } else {
      setNameDraft(DEFAULT_PROFILE_NAME);
    }
    setIsEditingName(false);
  }, [activeProfile, hasActiveProfile]);

  const commitNameEdit = useCallback(async () => {
    if (!hasActiveProfile) {
      setNameDraft((prev) => (prev.trim() ? prev.trim() : DEFAULT_PROFILE_NAME));
      setIsEditingName(false);
      return;
    }
    if (!activeProfile) return;
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setNameDraft(activeProfile.name);
      setIsEditingName(false);
      return;
    }
    if (trimmed === activeProfile.name) {
      setIsEditingName(false);
      return;
    }
    try {
      const result = await updateWatchlistMutateAsync({ id: activeProfile.id, name: trimmed });
      setActiveProfile({ ...activeProfile, name: result.name });
      setNameDraft(result.name);
      setWatchlistMessage(`Renamed to "${result.name}"`);
      refetchWatchlists();
    } catch (err) {
      setWatchlistError(err instanceof Error ? err.message : 'Unable to rename profile');
      setNameDraft(activeProfile.name);
    } finally {
      setIsEditingName(false);
    }
  }, [activeProfile, hasActiveProfile, nameDraft, refetchWatchlists, updateWatchlistMutateAsync]);

  const handleNameModalCancel = useCallback(() => {
    setIsNameModalOpen(false);
    setNameModalValue('');
    setNameModalError(null);
  }, []);

  const handleNameModalConfirm = useCallback(async () => {
    const trimmed = nameModalValue.trim();
    if (!trimmed) {
      setNameModalError('Name is required');
      return;
    }
    const exists = watchlistOptions.some((item) => item.name.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      setNameModalError('Profile name already exists');
      return;
    }
    const result = await handleCreateWatchlist(trimmed);
    if (result) {
      setIsNameModalOpen(false);
      setNameModalValue('');
      setNameModalError(null);
    }
  }, [handleCreateWatchlist, nameModalValue, watchlistOptions]);

  const formatWatchlistTime = useCallback((isoString) => {
    if (!isoString) return '';
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return isoString;
    }
  }, []);

  const appendLiveMessage = useCallback((message) => {
    setLiveMessages((prev) => [...prev.slice(-4), message]);
  }, []);

  const metadataOptions = useMemo(() => {
    const countries = new Set();
    const symbolTypes = new Set();
    const industries = new Set();
    if (Array.isArray(growthData)) {
      growthData.forEach((row) => {
        if (row.country) countries.add(String(row.country));
        if (row.symbol_type) symbolTypes.add(String(row.symbol_type));
        if (Array.isArray(row.industries)) {
          row.industries.forEach((ind) => {
            if (ind) industries.add(String(ind));
          });
        }
      });
    }
    const sortFn = (a, b) => a.localeCompare(b);
    return {
      countries: Array.from(countries).sort(sortFn),
      symbolTypes: Array.from(symbolTypes).sort(sortFn),
      industries: Array.from(industries).sort(sortFn),
    };
  }, [growthData]);

  const toggleMultiSelection = useCallback((option, current, allOptions) => {
    if (!option) return current;
    const normalizedOptions = Array.isArray(allOptions) ? allOptions : [];
    if (current === null) {
      const next = normalizedOptions.filter((item) => item !== option);
      return next.length ? next : [];
    }
    if (current.includes(option)) {
      const next = current.filter((item) => item !== option);
      return next.length ? next : [];
    }
    const next = [...current, option];
    return next.length === normalizedOptions.length ? null : next;
  }, []);

  const sanitizeSelection = useCallback((selection, allowed) => {
    if (selection === null) return null;
    if (!Array.isArray(selection)) return null;
    const filtered = selection.filter((item) => allowed.includes(item));
    if (!filtered.length) return [];
    if (filtered.length === allowed.length) return null;
    return filtered;
  }, []);

  useEffect(() => {
    setMetaFilters((prev) => {
      const nextCountry = sanitizeSelection(prev.country, metadataOptions.countries);
      const nextType = sanitizeSelection(prev.symbol_type, metadataOptions.symbolTypes);
      const nextIndustry = sanitizeSelection(prev.industries, metadataOptions.industries);
      if (
        nextCountry === prev.country &&
        nextType === prev.symbol_type &&
        nextIndustry === prev.industries
      ) {
        return prev;
      }
      return {
        ...prev,
        country: nextCountry,
        symbol_type: nextType,
        industries: nextIndustry,
      };
    });
  }, [metadataOptions.countries, metadataOptions.symbolTypes, metadataOptions.industries, sanitizeSelection]);

  const updateLiveRunStorage = useCallback((runId) => {
    if (typeof window === 'undefined') return;
    if (runId) {
      window.localStorage.setItem(LIVE_RUN_STORAGE_KEY, String(runId));
    } else {
      window.localStorage.removeItem(LIVE_RUN_STORAGE_KEY);
    }
  }, []);

  const resetLiveBuffers = useCallback(() => {
    setLiveEquityPoints([]);
    setLiveTradeEvents([]);
    setLiveMessages([]);
    setLiveResult(null);
    setLiveProgress(null);
    setLiveDailyStats([]);
  }, []);

  const upsertRunSummary = useCallback((summary) => {
    if (!summary || typeof summary.id !== 'number') return;
    setRunList((prev) => {
      const idx = prev.findIndex((item) => item.id === summary.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...summary };
        return next;
      }
      return [summary, ...prev];
    });
  }, []);

  const removeRunById = useCallback((runId) => {
    if (!runId) return;
    setRunList((prev) => prev.filter((run) => run.id !== runId));
  }, []);

  useEffect(() => () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!liveRunId) {
      setLiveStatus('idle');
      resetLiveBuffers();
      setLiveRunMeta(null);
      setLiveProgressState({ completed: 0, total: 0 });
    }
  }, [liveRunId, resetLiveBuffers]);

  useEffect(() => {
    activeRunIdRef.current = liveRunId;
  }, [liveRunId]);

  useEffect(() => {
    liveStatusRef.current = liveStatus;
  }, [liveStatus]);

  useEffect(() => {
    selectedRunIdRef.current = selectedRunId;
    if (!selectedRunId) {
      viewingRunIdRef.current = null;
      setViewedRunDetail(null);
    }
  }, [selectedRunId]);

  useEffect(() => {
    if (runDetailQuery.data && runDetailQuery.data.id === selectedRunIdRef.current) {
      viewingRunIdRef.current = runDetailQuery.data.id;
      setViewedRunDetail(runDetailQuery.data);
    }
  }, [runDetailQuery.data]);

  const timelineRunId = selectedRunId || liveRunId || null;

  useEffect(() => {
    setSelectedSymbol(null);
  }, [timelineRunId]);

  useEffect(() => {
    if (selectedWatchlistId == null) {
      return;
    }
    if (activeProfile?.id === selectedWatchlistId) {
      return;
    }
    const target = watchlistOptions.find((item) => item.id === selectedWatchlistId);
    if (!target) return;
    handleLoadWatchlist(target, { closePanel: false });
  }, [activeProfile?.id, handleLoadWatchlist, selectedWatchlistId, watchlistOptions]);

  const handleStopLossModeChange = useCallback((mode) => {
    setStopLossMode(mode);
    setActiveProfile((prev) => (prev ? {
      ...prev,
      state: {
        ...prev.state,
        stopLoss: { mode, value: prev.state?.stopLoss?.value ?? 0.05 },
      },
    } : prev));
  }, []);

  const handleChangeFilter = (key, value) => {
    setGrowthInputs((prev) => {
      const current = prev[key] ?? { comparator: 'gte', value: '', sell: { enabled: true } };
      if (current.value === value) return prev;
      return {
        ...prev,
        [key]: {
          ...current,
          value,
        },
      };
    });
  };

  const handleFundModeChange = (key, mode) => {
    setFundFilters((prev) => {
      const current = prev[key] || { mode: 'off', value: '' };
      if (current.mode === mode) return prev;
      return {
        ...prev,
        [key]: {
          mode,
          value: mode === 'gt' || mode === 'lt' ? current.value : '',
        },
      };
    });
  };

  const handleFundValueChange = (key, value) => {
    setFundFilters((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value,
      },
    }));
  };

  const handleCountryToggle = useCallback((option) => {
    setMetaFilters((prev) => ({
      ...prev,
      country: toggleMultiSelection(option, prev.country, metadataOptions.countries),
    }));
  }, [metadataOptions.countries, toggleMultiSelection]);

  const handleCountrySetAll = useCallback(() => {
    setMetaFilters((prev) => ({ ...prev, country: null }));
  }, []);

  const handleCountrySetNone = useCallback(() => {
    setMetaFilters((prev) => ({ ...prev, country: [] }));
  }, []);

  const handleTypeToggle = useCallback((option) => {
    setMetaFilters((prev) => ({
      ...prev,
      symbol_type: toggleMultiSelection(option, prev.symbol_type, metadataOptions.symbolTypes),
    }));
  }, [metadataOptions.symbolTypes, toggleMultiSelection]);

  const handleTypeSetAll = useCallback(() => {
    setMetaFilters((prev) => ({ ...prev, symbol_type: null }));
  }, []);

  const handleTypeSetNone = useCallback(() => {
    setMetaFilters((prev) => ({ ...prev, symbol_type: [] }));
  }, []);

  const handleIndustryToggle = useCallback((option) => {
    setMetaFilters((prev) => ({
      ...prev,
      industries: toggleMultiSelection(option, prev.industries, metadataOptions.industries),
    }));
  }, [metadataOptions.industries, toggleMultiSelection]);

  const handleIndustrySetAll = useCallback(() => {
    setMetaFilters((prev) => ({ ...prev, industries: null }));
  }, []);

  const handleIndustrySetNone = useCallback(() => {
    setMetaFilters((prev) => ({ ...prev, industries: [] }));
  }, []);

  const handleComparatorChange = (key, comparator) => {
    setGrowthInputs((prev) => {
      const current = prev[key] ?? { comparator: 'gte', value: '', sell: { enabled: true } };
      if (current.comparator === comparator) return prev;
      return {
        ...prev,
        [key]: {
          ...current,
          comparator,
        },
      };
    });
  };

  const handleSellConditionToggle = (key, enabled) => {
    setGrowthInputs((prev) => {
      const current = prev[key] ?? { comparator: 'gte', value: '', sell: { enabled: true } };
      const currentEnabled = current.sell?.enabled !== false;
      if (currentEnabled === enabled) return prev;
      return {
        ...prev,
        [key]: {
          ...current,
          sell: { enabled },
        },
      };
    });
  };

  const handleSelectUniverseSymbol = useCallback((symbol) => {
    if (!symbol) return;
    setSelectedSymbol(symbol);
  }, []);

  const flushPendingMessages = useCallback((socket) => {
    if (!socket) return;
    while (pendingMessagesRef.current.length > 0) {
      const message = pendingMessagesRef.current.shift();
      if (!message) continue;
      try {
        socket.send(JSON.stringify(message));
      } catch (err) {
        pendingMessagesRef.current.unshift(message);
        break;
      }
    }
  }, []);

  const ensureWebSocket = useCallback(() => {
    if (typeof window === 'undefined') {
      setFormError('Live mode is not available in this environment.');
      return;
    }
    const existing = wsRef.current;
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const path = buildUrl('/v1/backtesting/ws');
    const wsUrl = `${protocol}://${window.location.host}${path}`;
    try {
      const socket = new WebSocket(wsUrl);
      wsRef.current = socket;

      socket.onopen = () => {
        if (pendingMessagesRef.current.length === 0) {
          if (activeRunIdRef.current) {
            pendingMessagesRef.current.push({ action: 'subscribe', run_id: activeRunIdRef.current });
          } else {
            pendingMessagesRef.current.push({ action: 'ping' });
          }
        }
        setFormError(null);
        flushPendingMessages(socket);
      };

      socket.onmessage = (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch (err) {
          return;
        }
        if (!message || typeof message !== 'object') return;
        setFormError(null);
        switch (message.type) {
          case 'started':
            if (message.payload?.id) {
              setLiveRunId(message.payload.id);
              updateLiveRunStorage(message.payload.id);
              activeRunIdRef.current = message.payload.id;
            }
            setLiveRunMeta(message.payload || null);
            setLiveStatus('running');
            appendLiveMessage({ state: 'started', detail: `Run #${message.payload?.id} started` });
            upsertRunSummary(message.payload);
            break;
          case 'snapshot':
            if (message.payload?.run) {
              setLiveRunMeta(message.payload.run);
              upsertRunSummary(message.payload.run);
              const snapshotRunId = message.payload.run.id;
              if (snapshotRunId && viewingRunIdRef.current === snapshotRunId && snapshotRunId !== activeRunIdRef.current) {
                setViewedRunDetail((prev) => ({
                  ...prev,
                  id: snapshotRunId,
                  name: message.payload.run?.name,
                  status: message.payload.run?.status,
                  summary: prev?.summary || null,
                  universe: prev?.universe || null,
                  equity_curve: message.payload?.equity_curve || [],
                  events: message.payload?.events || [],
                  daily_stats: message.payload?.daily_stats || [],
                }));
              }
            }
            setLiveEquityPoints(message.payload?.equity_curve || []);
            setLiveTradeEvents(message.payload?.events || []);
            setLiveDailyStats(message.payload?.daily_stats || []);
            setLiveStatus((prev) => (prev === 'connecting' ? 'running' : prev));
            break;
          case 'equity':
            if (message.payload) {
              setLiveEquityPoints((prev) => [...prev.slice(-999), message.payload]);
              setLiveProgress({ date: message.payload.date, equity: message.payload.equity });
            }
            break;
          case 'event':
            if (message.payload) {
              setLiveTradeEvents((prev) => [...prev.slice(-499), message.payload]);
            }
            break;
          case 'daily':
            if (message.payload) {
              setLiveDailyStats((prev) => [...prev.slice(-199), message.payload]);
            }
            break;
          case 'progress':
            if (message.payload) {
              setLiveProgressState({
                completed: message.payload.completed_days || 0,
                total: message.payload.total_days || 0,
              });
            }
            break;
          case 'status':
            appendLiveMessage(message.payload || message);
            if (message.payload?.state === 'cancelling') {
              setLiveStatus('cancelling');
            }
            break;
          case 'complete':
            setLiveResult(message.payload);
            setLiveEquityPoints(message.payload?.equity_curve || []);
            setLiveTradeEvents(message.payload?.events || []);
            setLiveDailyStats(message.payload?.daily_stats || []);
            setLiveStatus('complete');
            setLiveProgressState({ completed: 0, total: 0 });
            appendLiveMessage({ state: 'complete', detail: 'Run finished' });
            updateLiveRunStorage(null);
            setLiveRunId(null);
            activeRunIdRef.current = null;
            if (message.payload?.id && viewingRunIdRef.current === message.payload.id) {
              setViewedRunDetail(message.payload);
            }
            break;
          case 'cancelled':
            setLiveStatus('cancelled');
            appendLiveMessage({ state: 'cancelled', detail: 'Run cancelled' });
            updateLiveRunStorage(null);
            setLiveRunId(null);
            activeRunIdRef.current = null;
            setLiveProgressState({ completed: 0, total: 0 });
            break;
          case 'error':
            setFormError(typeof message.detail === 'string' ? message.detail : 'Live backtest failed.');
            setLiveStatus('error');
            appendLiveMessage({ state: 'error', detail: message.detail || 'Unexpected error' });
            updateLiveRunStorage(null);
            setLiveRunId(null);
            activeRunIdRef.current = null;
            setLiveProgressState({ completed: 0, total: 0 });
            break;
          case 'run_updated':
            upsertRunSummary(message.payload);
            if (message.payload?.id && viewingRunIdRef.current === message.payload.id && message.payload.id !== activeRunIdRef.current) {
              setViewedRunDetail((prev) => ({
                ...prev,
                ...message.payload,
              }));
            }
            break;
          case 'run_deleted':
            {
              const deletedId = Number(message.payload?.run_id);
              if (deletedId) {
                removeRunById(deletedId);
                if (activeRunIdRef.current === deletedId) {
                  setLiveRunId(null);
                  updateLiveRunStorage(null);
                  activeRunIdRef.current = null;
                  needsInitialSubscribe.current = false;
                  setLiveStatus('idle');
                  resetLiveBuffers();
                }
                if (selectedRunIdRef.current === deletedId) {
                  setSelectedRunId(null);
                  viewingRunIdRef.current = null;
                  setViewedRunDetail(null);
                }
              }
            }
            break;
          case 'pong':
            break;
          default:
            break;
        }
      };

      socket.onerror = () => {
        setFormError('WebSocket connection failed.');
        setLiveStatus('error');
        setLiveRunId(null);
        activeRunIdRef.current = null;
        needsInitialSubscribe.current = false;
        updateLiveRunStorage(null);
        resetLiveBuffers();
      };

      socket.onclose = () => {
        if (wsRef.current === socket) {
          wsRef.current = null;
        }
        if (activeRunIdRef.current) {
          setLiveStatus('connecting');
        } else if (liveStatusRef.current !== 'running') {
          setLiveStatus('idle');
        }
        reconnectTimerRef.current = setTimeout(() => {
          ensureWebSocket();
        }, 1500);
      };
    } catch (err) {
      setFormError('Unable to open WebSocket connection.');
    }
  }, [appendLiveMessage, flushPendingMessages, removeRunById, resetLiveBuffers, upsertRunSummary, updateLiveRunStorage]);

  const sendMessage = useCallback((payload) => {
    if (!payload) return;
    const socket = wsRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(JSON.stringify(payload));
      } catch (err) {
        pendingMessagesRef.current.push(payload);
      }
    } else {
      pendingMessagesRef.current.push(payload);
      ensureWebSocket();
    }
  }, [ensureWebSocket]);

  useEffect(() => {
    ensureWebSocket();
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          /* noop */
        }
        wsRef.current = null;
      }
    };
  }, [ensureWebSocket]);

  const subscribeToRun = useCallback((runId, options = {}) => {
    if (!runId) return;
    if (!options.forViewing) {
      setLiveStatus('connecting');
      resetLiveBuffers();
    }
    sendMessage({ action: 'subscribe', run_id: runId });
  }, [resetLiveBuffers, sendMessage]);

  useEffect(() => {
    if (!liveRunId) return;
    if (!needsInitialSubscribe.current) return;
    needsInitialSubscribe.current = false;
    subscribeToRun(liveRunId);
  }, [liveRunId, subscribeToRun]);

  const handleCancelLive = useCallback(() => {
    if (!liveRunId) return;
    setLiveStatus('cancelling');
    sendMessage({ action: 'cancel', run_id: liveRunId });
  }, [liveRunId, sendMessage]);

  const growthSummaryValues = useMemo(() => buildGrowthSummary(growthInputs), [growthInputs]);
  const fundSummaryValues = useMemo(
    () => buildFundSummary(fundFilters),
    [fundFilters],
  );
  const metaSummaryValues = useMemo(() => buildMetaSummary(metaFilters), [metaFilters]);

  const handleRun = () => {
    setFormError(null);
    setSelectedRunId(null);
    viewingRunIdRef.current = null;
    setViewedRunDetail(null);
    needsInitialSubscribe.current = false;
    if (!activeProfile) {
      setFormError('Select a profile to run backtesting.');
      return;
    }
    const frequency = Number(frequencyInput);
    if (!Number.isFinite(frequency) || frequency < 1) {
      setFormError('Buy frequency must be 1 day or greater.');
      return;
    }
    if (backtestStartDate && backtestEndDate && backtestStartDate > backtestEndDate) {
      setFormError('End date must be on or after start date.');
      return;
    }
    const stopLoss = activeProfile?.state?.stopLoss || { mode: 'trailing', value: 0.05 };
    const payload = {
      buy_frequency_days: Math.floor(frequency),
      growth_filters: normalizeGrowthFilters(growthInputs),
      meta_filters: metaFilters,
      fundamental_filters: fundFilters,
      symbol_whitelist: pinnedSymbols,
      restrict_to_symbol_whitelist: restrictPinned && pinnedSymbols.length > 0,
      profile_id: activeProfile.id,
      profile_name: activeProfile.name,
      stop_loss_mode: stopLoss.mode,
      stop_loss_value: stopLoss.mode === 'fixed_drop' ? stopLoss.value : undefined,
      start_date: backtestStartDate || undefined,
      end_date: backtestEndDate || undefined,
    };
    setLastRunPayload(payload);
    setLiveStatus('connecting');
    resetLiveBuffers();
    setLiveRunMeta(null);
    sendMessage({ action: 'start', payload });
  };

  const handleViewRun = (run) => {
    viewingRunIdRef.current = run.id;
    setSelectedRunId(run.id);
    setViewedRunDetail(null);
    subscribeToRun(run.id, { forViewing: true });
  };

  const handleResumeRun = (run) => {
    setSelectedRunId(null);
    setLiveRunId(run.id);
    updateLiveRunStorage(run.id);
    needsInitialSubscribe.current = false;
    viewingRunIdRef.current = null;
    setViewedRunDetail(null);
    subscribeToRun(run.id, { forViewing: false });
  };

  const handleRenameRun = (run) => {
    if (typeof window === 'undefined') return;
    const next = window.prompt('Rename backtest', run.name);
    if (!next) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === run.name) return;
    sendMessage({ action: 'rename', run_id: run.id, name: trimmed });
  };

  const handleDeleteRun = (run) => {
    if (typeof window === 'undefined') return;
    const confirmed = window.confirm(`Delete backtest "${run.name}"? This cannot be undone.`);
    if (!confirmed) return;
    sendMessage({ action: 'delete', run_id: run.id });
  };

  const disablePinnedToggle = pinnedSymbols.length === 0;
  const isLiveInFlight = Boolean(liveRunId) && (liveStatus === 'running' || liveStatus === 'connecting' || liveStatus === 'cancelling');

  let summaryData = null;
  let universeData = null;
  let chartData = [];
  let eventData = [];
  let openPositions = [];
  let auditData = [];

  const resultInputs = useMemo(() => {
    if (liveResult?.inputs) return liveResult.inputs;
    if (selectedRunDetail?.inputs) return selectedRunDetail.inputs;
    return lastRunPayload;
  }, [liveResult?.inputs, selectedRunDetail?.inputs, lastRunPayload]);

  const resultGrowthSummary = useMemo(
    () => (resultInputs ? buildGrowthSummary(resultInputs.growth_filters) : []),
    [resultInputs],
  );
  const resultFundSummary = useMemo(
    () => (resultInputs ? buildFundSummary(resultInputs.fundamental_filters) : []),
    [resultInputs],
  );
  const resultMetaSummary = useMemo(
    () => (resultInputs ? buildMetaSummary(resultInputs.meta_filters) : []),
    [resultInputs],
  );
  const resultPinnedSymbols = resultInputs?.symbol_whitelist || [];
  const resultRestrictPinned = Boolean(resultInputs?.restrict_to_symbol_whitelist);
  const resultTabs = useMemo(
    () => [
      { id: 'equity', label: 'Equity Curve' },
      { id: 'symbols', label: 'Bought Symbols' },
      { id: 'trades', label: 'Trade Timeline' },
      { id: 'audit', label: 'Daily Audit' },
    ],
    [],
  );

  const runResultsLoading = useMemo(
    () =>
      Boolean(
        !liveRunId &&
        selectedRunId &&
        isRunDetailLoading &&
        !selectedRunDetail,
      ),
    [liveRunId, selectedRunId, isRunDetailLoading, selectedRunDetail],
  );

  const availabilityPoints = useMemo(
    () => (Array.isArray(availabilityData)
      ? availabilityData.map((row) => ({
        date: row.date,
        symbols: Number(row.symbols) || 0,
      }))
      : []),
    [availabilityData],
  );

  if (liveRunId || liveStatus !== 'idle') {
    if (liveResult) {
      summaryData = liveResult.summary;
      universeData = liveResult.universe;
      chartData = liveResult.equity_curve || [];
      eventData = liveResult.events || [];
      openPositions = liveResult.summary?.open_positions || [];
      auditData = liveResult.daily_stats || [];
    } else {
      chartData = liveEquityPoints;
      eventData = liveTradeEvents;
      auditData = liveDailyStats;
    }
  } else if (selectedRunDetail) {
    summaryData = selectedRunDetail.summary;
    universeData = selectedRunDetail.universe;
    chartData = selectedRunDetail.equity_curve || [];
    eventData = selectedRunDetail.events || [];
    openPositions = summaryData?.open_positions || [];
    auditData = selectedRunDetail.daily_stats || [];
  }

  const liveSymbols = (() => {
    const seen = new Set();
    const next = [];
    const pushSymbol = (raw) => {
      const symbol = String(raw || '').trim().toUpperCase();
      if (!symbol || seen.has(symbol)) return;
      seen.add(symbol);
      next.push(symbol);
    };

    (eventData || []).forEach((event) => pushSymbol(event?.symbol));
    (openPositions || []).forEach((position) => pushSymbol(position?.symbol));
    (universeData?.symbols || []).forEach((symbol) => pushSymbol(symbol));
    (universeData?.skipped_symbols || []).forEach((entry) => pushSymbol(entry?.symbol));
    (auditData || []).forEach((day) => {
      (day?.buys || []).forEach((trade) => pushSymbol(trade?.symbol));
      (day?.sells || []).forEach((trade) => pushSymbol(trade?.symbol));
      (day?.holdings || []).forEach((holding) => pushSymbol(holding?.symbol));
    });
    if (selectedSymbol) {
      pushSymbol(selectedSymbol);
    }
    return next;
  })();
  const { data: livePricePayload } = useLivePricesQuery(liveSymbols, {
    enabled: liveSymbols.length > 0,
  });
  const livePriceMap = livePricePayload?.prices || {};

  return (
    <div className="backtesting-view">
      <section className="backtest-panel profile-panel">
        <div className="panel-header">
          <div>
            <h3>Profile & Simulation</h3>
            <p>Select a profile, tune the gates, and run a backtest.</p>
          </div>
          <div className="profile-controls">
            <div className="watchlist-name-block">
              <span className="watchlist-name-label">Profile:</span>
              {isEditingName ? (
                <input
                  ref={nameInputRef}
                  className="watchlist-name-input"
                  value={nameDraft}
                  onChange={(evt) => setNameDraft(evt.target.value)}
                  onBlur={commitNameEdit}
                  onKeyDown={(evt) => {
                    if (evt.key === 'Enter') {
                      evt.preventDefault();
                      commitNameEdit();
                    } else if (evt.key === 'Escape') {
                      evt.preventDefault();
                      cancelNameEdit();
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="watchlist-name-display"
                  onClick={() => setIsEditingName(true)}
                  aria-label={hasActiveProfile ? 'Rename profile' : 'Set profile name'}
                >
                  <span className="watchlist-name-value">{nameDraft}</span>
                  <span className="watchlist-name-pencil" aria-hidden="true">✎</span>
                </button>
              )}
              <button className="ghost xs" type="button" onClick={handleNewProfile}>
                New
              </button>
              {!hasActiveProfile ? (
                <span className="watchlist-name-unsaved">(unsaved)</span>
              ) : null}
            </div>
            <div className="profile-actions-row">
              <button
                className="ghost sm"
                type="button"
                onClick={handleSaveWatchlist}
                disabled={savingProfile || (!hasChangesFromActive && hasActiveProfile)}
              >
                {savingProfile ? 'Saving…' : 'Save'}
              </button>
              <button className="ghost sm" type="button" onClick={() => setIsWatchlistOpen(true)}>
                Manage Profiles
              </button>
              <div className="profile-select">
                {watchlistsLoading ? (
                  <span>Loading profiles…</span>
                ) : watchlistOptions.length ? (
                  <select
                    value={selectedWatchlistId ?? ''}
                    onChange={(e) => {
                      const nextId = e.target.value ? Number(e.target.value) : null;
                      if (!Number.isFinite(nextId)) {
                        handleNewProfile();
                        return;
                      }
                      setSelectedWatchlistId(nextId);
                    }}
                  >
                    <option value="" disabled>
                      Select a profile…
                    </option>
                    {watchlistOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span>No profiles yet. Create one on the Growth tab.</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="watchlist-status">
          {hasActiveProfile && hasChangesFromActive ? (
            <span className="watchlist-status__dirty">Unsaved changes</span>
          ) : null}
          {watchlistError ? <span className="error-text">{watchlistError}</span> : null}
        </div>
        {watchlistsIsError && (
          <div className="form-error">{watchlistsError?.message || 'Unable to load watchlists.'}</div>
        )}
        <div className="profile-meta">
          <div>
            <strong>Profile:</strong> {activeProfile ? activeProfile.name : '—'}
          </div>
          <div>
            <strong>Pinned symbols:</strong> {pinnedSymbols.length || 0}
          </div>
          <div>
            <strong>Stop loss:</strong>
            <select
              value={stopLossMode}
              onChange={(e) => handleStopLossModeChange(e.target.value)}
              disabled={!activeProfile}
            >
              <option value="trailing">Trailing (5% from peak)</option>
              <option value="fixed_drop">Fixed 5% drop from entry</option>
            </select>
          </div>
          <div className="date-range-input-group">
            <label>
              <span>Start date</span>
              <input
                type="date"
                value={backtestStartDate}
                onChange={(e) => setBacktestStartDate(e.target.value)}
                max={backtestEndDate || undefined}
              />
            </label>
            <label>
              <span>End date</span>
              <input
                type="date"
                value={backtestEndDate}
                onChange={(e) => setBacktestEndDate(e.target.value)}
                min={backtestStartDate || undefined}
              />
            </label>
          </div>
        </div>
        <div className="input-grid">
          {PERIODS.map(({ key, label }) => {
            const current = growthInputs[key] || { comparator: 'gte', value: '', sell: { enabled: true } };
            const sellEnabled = current.sell?.enabled !== false;
            return (
              <div key={key} className="input-field growth-filter">
                <span>{label} Growth</span>
                <div className="growth-filter__controls">
                  <span className="filter-input-group">
                    <select
                      value={current.comparator}
                      onChange={(e) => handleComparatorChange(key, e.target.value)}
                    >
                      {GROWTH_COMPARATORS.map((mode) => (
                        <option key={mode.value} value={mode.value}>
                          {mode.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.1"
                      value={current.value}
                      onChange={(e) => handleChangeFilter(key, e.target.value)}
                      placeholder="%"
                    />
                  </span>
                  <label className="sell-toggle">
                    <input
                      type="checkbox"
                      checked={sellEnabled}
                      onChange={(e) => handleSellConditionToggle(key, e.target.checked)}
                    />
                    <span>Sell</span>
                  </label>
                </div>
              </div>
            );
          })}
          <label className="input-field">
            <span>Buy Frequency (days)</span>
            <input
              type="number"
              min="1"
              value={frequencyInput}
              onChange={(e) => setFrequencyInput(e.target.value)}
            />
          </label>
        </div>
        <div className="input-grid fundamentals-grid">
          {FUNDAMENTAL_METRICS.map(({ key, label, step }) => {
            const config = fundFilters[key] || { mode: 'off', value: '' };
            const needsValue = config.mode === 'gt' || config.mode === 'lt';
            return (
              <div key={key} className="input-field fund-filter">
                <span>{label}</span>
                <span className="filter-input-group">
                  <select value={config.mode} onChange={(e) => handleFundModeChange(key, e.target.value)}>
                    {FUND_FILTER_MODES.map((mode) => (
                      <option key={mode.value} value={mode.value}>
                        {mode.label}
                      </option>
                    ))}
                  </select>
                  {needsValue ? (
                    <input
                      type="number"
                      step={step ?? '0.01'}
                      value={config.value}
                      onChange={(e) => handleFundValueChange(key, e.target.value)}
                      placeholder="0"
                    />
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
        <div className="meta-filters-row">
          <MultiSelectFilter
            label="Country"
            options={metadataOptions.countries}
            selected={metaFilters.country}
            search={countrySearch}
            onSearchChange={setCountrySearch}
            onToggle={handleCountryToggle}
            onSetAll={handleCountrySetAll}
            onSetNone={handleCountrySetNone}
          />
          <MultiSelectFilter
            label="Type"
            options={metadataOptions.symbolTypes}
            selected={metaFilters.symbol_type}
            search={symbolTypeSearch}
            onSearchChange={setSymbolTypeSearch}
            onToggle={handleTypeToggle}
            onSetAll={handleTypeSetAll}
            onSetNone={handleTypeSetNone}
          />
          <MultiSelectFilter
            label="Industry"
            options={metadataOptions.industries}
            selected={metaFilters.industries}
            search={industrySearch}
            onSearchChange={setIndustrySearch}
            onToggle={handleIndustryToggle}
            onSetAll={handleIndustrySetAll}
            onSetNone={handleIndustrySetNone}
          />
        </div>
        {(fundSummaryValues.length > 0 || metaSummaryValues.length > 0) && (
          <div className="input-summary">
            {fundSummaryValues.length > 0 && (
              <div>
                <strong>Fundamental filters:</strong>
                <div className="input-summary__list">{fundSummaryValues.join(', ')}</div>
              </div>
            )}
            {metaSummaryValues.length > 0 && (
              <div>
                <strong>Meta filters:</strong>
                <div className="input-summary__list">{metaSummaryValues.join(', ')}</div>
              </div>
            )}
          </div>
        )}
        <AvailabilityChart data={availabilityPoints} loading={availabilityLoading} />
        <label className={`toggle ${disablePinnedToggle ? 'toggle--disabled' : ''}`}>
          <input
            type="checkbox"
            disabled={disablePinnedToggle}
            checked={restrictPinned && !disablePinnedToggle}
            onChange={(e) => setRestrictPinned(e.target.checked)}
          />
          <span>Limit universe to pinned symbols ({pinnedSymbols.length || 0})</span>
        </label>
        <div className="live-status-bar">
          <span className={`live-pill live-pill--${liveStatus}`}>
            {liveStatus}
          </span>
          {liveRunMeta?.name && (
            <span className="live-status-details">Run: {liveRunMeta.name}</span>
          )}
          {liveProgress && (
            <span className="live-status-details">
              Last day processed: {liveProgress.date}
            </span>
          )}
          {liveProgressPercent > 0 && (
            <div className="progress-bar">
              <div className="progress-bar__fill" style={{ width: `${liveProgressPercent}%` }} />
              <span className="progress-bar__label">
                {liveProgressState.completed}/{liveProgressState.total} days
              </span>
            </div>
          )}
          {liveMessages.length > 0 && (
            <span className="live-status-details">
              {(liveMessages[liveMessages.length - 1]?.detail)
                || (liveMessages[liveMessages.length - 1]?.state)
                || 'Streaming'}
            </span>
          )}
          {isLiveInFlight && (
            <button type="button" className="ghost xs" onClick={handleCancelLive}>
              Cancel live run
            </button>
          )}
        </div>
        {formError && <div className="form-error">{formError}</div>}
        <button
          type="button"
          className="run-button"
          onClick={handleRun}
          disabled={!activeProfile || isLiveInFlight}
        >
          {isLiveInFlight ? 'Streaming…' : 'Start Backtest'}
        </button>
      </section>

      <section className="backtest-panel">
        <div className="panel-header">
          <div>
            <h3>Backtest History</h3>
            <p>Browse runs and expand a row to review the results.</p>
          </div>
          <button type="button" className="ghost xs" onClick={() => refetchRuns()}>
            Refresh
          </button>
        </div>
        {runsLoading ? (
          <div>Loading runs…</div>
        ) : runsIsError ? (
          <div className="form-error">{runsError?.message || 'Unable to load backtest runs.'}</div>
        ) : runList.length === 0 ? (
          <div>No prior runs yet.</div>
        ) : (
          <div className="run-history">
            {runList.map((run) => {
              const isOpen = selectedRunId === run.id;
              const hasDetail = isOpen && summaryData;
              return (
                <div key={run.id} className={`run-row${isOpen ? ' run-row--open' : ''}`}>
                  <div className="run-row-main">
                    <strong>{run.name}</strong>
                    <span className={`status-pill status-pill--${run.status}`}>
                      {run.status}
                    </span>
                  </div>
                  <div className="run-row-meta">
                    <span>{run.profile_name || 'Profile unknown'}</span>
                    {run.created_at && <span>{new Date(run.created_at).toLocaleString()}</span>}
                    {run.final_equity != null && <span>Equity {formatCurrency(run.final_equity)}</span>}
                  </div>
                  <div className="run-row-actions">
                    <button
                      type="button"
                      className="ghost xs"
                      onClick={() => handleViewRun(run)}
                      disabled={selectedRunId === run.id && isRunDetailLoading}
                    >
                      {selectedRunId === run.id && isRunDetailLoading ? 'Loading…' : 'View'}
                    </button>
                    {run.status === 'running' && (
                      <button type="button" className="ghost xs" onClick={() => handleResumeRun(run)}>
                        Resume
                      </button>
                    )}
                    <button type="button" className="ghost xs" onClick={() => handleRenameRun(run)}>
                      Rename
                    </button>
                    <button
                      type="button"
                      className="ghost xs danger"
                      onClick={() => handleDeleteRun(run)}
                    >
                      Delete
                    </button>
                  </div>
                  {isOpen && (
                    <div className="run-row-expanded">
                      {runResultsLoading ? (
                        <div className="backtest-panel empty">Loading backtest…</div>
                      ) : hasDetail ? (
                        <>
                          <BacktestSummary summary={summaryData} />
                          {resultInputs && (
                            <FilterSummary
                              growthFilters={resultGrowthSummary}
                              fundFilters={resultFundSummary}
                              metaFilters={resultMetaSummary}
                              pinnedSymbols={resultPinnedSymbols}
                              restrictPinned={resultRestrictPinned}
                            />
                          )}
                          <div className="results-tabs">
                            {resultTabs.map((tab) => (
                              <button
                                key={tab.id}
                                type="button"
                                className={`results-tab${resultsTab === tab.id ? ' active' : ''}`}
                                onClick={() => setResultsTab(tab.id)}
                              >
                                {tab.label}
                              </button>
                            ))}
                          </div>
                          <div className="results-tabpanel">
                            {resultsTab === 'symbols' && (
                              <div className="symbols-tab">
                                <BacktestUniverseCard
                                  universe={universeData}
                                  onSelectSymbol={handleSelectUniverseSymbol}
                                  activeSymbol={selectedSymbol}
                                  livePriceMap={livePriceMap}
                                />
                                <SymbolTimelinePanel
                                  runId={timelineRunId}
                                  symbol={selectedSymbol}
                                  livePriceMap={livePriceMap}
                                />
                              </div>
                            )}
                            {resultsTab === 'equity' && (
                              <>
                                <BacktestChart data={chartData} />
                                <BacktestPositions positions={openPositions} livePriceMap={livePriceMap} />
                              </>
                            )}
                            {resultsTab === 'trades' && <BacktestEvents events={eventData} livePriceMap={livePriceMap} />}
                            {resultsTab === 'audit' && <BacktestAuditTable rows={auditData} livePriceMap={livePriceMap} />}
                          </div>
                        </>
                      ) : (
                        <div className="backtest-panel empty">Select this run to view details.</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
      {isWatchlistOpen
        ? ReactDOM.createPortal(
          <div className="watchlist-overlay" role="dialog" aria-modal="true">
            <div className="watchlist-backdrop" onClick={() => setIsWatchlistOpen(false)} />
            <div className="watchlist-panel" onClick={(event) => event.stopPropagation()}>
              <header className="watchlist-header">
                <div>
                  <h3>Profiles</h3>
                  <p className="subtle">
                    {activeProfile ? `Active: ${activeProfile.name}` : 'Load and manage saved profiles'}
                  </p>
                </div>
                <button className="ghost sm" type="button" onClick={() => setIsWatchlistOpen(false)}>
                  Close
                </button>
              </header>
              {watchlistsFetchError ? (
                <div className="error-box" style={{ marginBottom: '0.75rem' }}>
                  <p>Unable to load profiles.</p>
                  <p className="subtle" style={{ marginTop: '.25rem' }}>{watchlistsFetchError}</p>
                </div>
              ) : null}
              {watchlistError ? (
                <div className="error-box" style={{ marginBottom: '0.75rem' }}>
                  <p>{watchlistError}</p>
                </div>
              ) : null}
              <section className="watchlist-section">
                <div className="watchlist-list-header">
                  <h4>Existing profiles</h4>
                  <button
                    className="ghost xs"
                    type="button"
                    onClick={() => refetchWatchlists()}
                    disabled={watchlistsLoading}
                  >
                    {watchlistsLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>
                {watchlistsLoading ? (
                  <p className="subtle">Loading profiles…</p>
                ) : watchlistOptions.length === 0 ? (
                  <p className="subtle">No profiles yet.</p>
                ) : (
                  <ul className="watchlist-items">
                    {watchlistOptions.map((watchlist) => {
                      const isActive = activeProfile?.id === watchlist.id;
                      const isDeleting = pendingDeleteId === watchlist.id;
                      const handleClick = () => handleLoadWatchlist(watchlist);
                      return (
                        <li
                          key={watchlist.id}
                          className={`watchlist-item${isActive ? ' active' : ''}`}
                          onClick={handleClick}
                        >
                          <button type="button" className="watchlist-item-meta" onClick={handleClick}>
                            <strong>{watchlist.name}</strong>
                            <span className="subtle">Updated {formatWatchlistTime(watchlist.updated_at)}</span>
                          </button>
                          <div className="watchlist-item-actions">
                            <button
                              className="ghost xs danger"
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDeleteWatchlist(watchlist);
                              }}
                              disabled={isDeleting}
                            >
                              {isDeleting ? 'Deleting…' : 'Delete'}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          </div>,
          document.body,
        )
        : null}
      {isNameModalOpen
        ? ReactDOM.createPortal(
          <div className="name-modal-overlay" role="dialog" aria-modal="true">
            <div className="name-modal-backdrop" onClick={handleNameModalCancel} />
            <div className="name-modal" onClick={(event) => event.stopPropagation()}>
              <h3>Name this profile</h3>
              <p className="subtle">Choose a unique name so you can load it later.</p>
              <input
                ref={nameModalInputRef}
                className="name-modal-input"
                value={nameModalValue}
                onChange={(evt) => setNameModalValue(evt.target.value)}
              />
              {nameModalError ? <p className="error-text">{nameModalError}</p> : null}
              <div className="name-modal-actions">
                <button className="ghost sm" type="button" onClick={handleNameModalCancel}>
                  Cancel
                </button>
                <button className="ghost sm" type="button" onClick={handleNameModalConfirm}>
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )
        : null}
      {watchlistMessage ? (
        <div className="toast toast-success" role="status">
          {watchlistMessage}
        </div>
      ) : null}
    </div>
  );
}
